import { useState, useEffect } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  ArrowLeft,
  Clock,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Loader2,
  DollarSign,
  Brain,
  Activity,
  Zap,
  Copy,
  TrendingUp,
  Target,
  Lightbulb,
  ExternalLink,
  Info,
} from "lucide-react"
import { toast } from "sonner"
import { agentsService, type AgentExecution, type Agent } from "@/services/agents"
import { useOrganization } from "@/contexts/organization-context"

// Execution status configuration
const statusConfig: Record<string, { icon: typeof CheckCircle2; color: string; bgColor: string; label: string }> = {
  running: { icon: Loader2, color: "text-blue-600", bgColor: "bg-blue-50", label: "Running" },
  completed: { icon: CheckCircle2, color: "text-emerald-600", bgColor: "bg-emerald-50", label: "Completed" },
  failed: { icon: XCircle, color: "text-red-600", bgColor: "bg-red-50", label: "Failed" },
  cancelled: { icon: AlertCircle, color: "text-amber-600", bgColor: "bg-amber-50", label: "Cancelled" },
}

// NOTE: Agents are now pure functions that produce outputs (predictions) only.
// The Optimizer decides what actions to take based on these outputs.
// Action-related code has been removed as part of the Agent Architecture Refactor (Phase 10).

export function ExecutionDetailPage() {
  const { agentId, executionId } = useParams<{ agentId: string; executionId: string }>()
  const navigate = useNavigate()
  const { currentOrganization } = useOrganization()

  const [execution, setExecution] = useState<AgentExecution | null>(null)
  const [agent, setAgent] = useState<Agent | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (executionId && agentId) {
      loadExecutionData()
    }
  }, [executionId, agentId])

  const loadExecutionData = async () => {
    if (!executionId || !agentId) return

    setLoading(true)
    try {
      // Agents now only produce outputs (predictions), not actions
      // The decision field contains the agent's analysis and output predictions
      const [execData, agentData] = await Promise.all([
        agentsService.getExecution(parseInt(executionId)),
        agentsService.getAgent(parseInt(agentId)),
      ])
      setExecution(execData)
      setAgent(agentData)
    } catch (error: any) {
      console.error('Error loading execution:', error)
      toast.error(`Failed to load execution: ${error.response?.data?.detail || error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleCancelExecution = async () => {
    if (!executionId || execution?.status !== 'running') return

    try {
      await agentsService.cancelExecution(parseInt(executionId))
      toast.success("Execution cancelled")
      loadExecutionData()
    } catch (error: any) {
      toast.error(`Failed to cancel: ${error.response?.data?.detail || error.message}`)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success("Copied to clipboard")
  }

  const formatDuration = (ms: number | null) => {
    if (!ms) return "-"
    if (ms < 1000) return `${ms}ms`
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
    return `${(ms / 60000).toFixed(1)}m`
  }

  const formatCost = (cost: number | null) => {
    if (!cost) return "-"
    return `$${cost.toFixed(4)}`
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
        <p className="text-slate-500">Loading execution...</p>
      </div>
    )
  }

  if (!execution) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <AlertCircle className="h-10 w-10 text-red-500" />
        <p className="text-slate-500">Execution not found</p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    )
  }

  const status = statusConfig[execution.status] || statusConfig.completed
  const StatusIcon = status.icon

  // Parse decision if it's a string
  let decision: any = null
  try {
    decision = typeof execution.decision === 'string'
      ? JSON.parse(execution.decision)
      : execution.decision
  } catch (e) {
    decision = execution.decision
  }

  // Extract outputs from decision (new pure function model)
  const outputs = decision?.outputs || {}
  const outputKeys = Object.keys(outputs)

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">Execution #{execution.id}</h1>
              <Badge
                variant="secondary"
                className={`${status.bgColor} ${status.color} border-0 gap-1`}
              >
                <StatusIcon className={`h-3 w-3 ${execution.status === 'running' ? 'animate-spin' : ''}`} />
                {status.label}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
              <span>Agent: {agent?.name || `#${execution.agent_id}`}</span>
              <span>Trigger: {execution.trigger_type}</span>
              {execution.started_at && (
                <span>{new Date(execution.started_at).toLocaleString()}</span>
              )}
            </div>
          </div>
          {execution.status === 'running' && (
            <Button variant="destructive" onClick={handleCancelExecution}>
              Cancel Execution
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">{formatDuration(execution.duration_ms)}</p>
                  <p className="text-xs text-slate-500">Duration</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Brain className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="text-sm font-mono text-slate-900 truncate max-w-[100px] cursor-help">
                        {execution.llm_model_used?.split('-').slice(0, 2).join('-') || '-'}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{execution.llm_model_used || 'No model specified'}</p>
                    </TooltipContent>
                  </Tooltip>
                  <p className="text-xs text-slate-500">Model</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Activity className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">
                    {(execution.tokens_input || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-500">Input Tokens</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Zap className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">
                    {(execution.tokens_output || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-500">Output Tokens</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">{formatCost(execution.llm_cost_usd)}</p>
                  <p className="text-xs text-slate-500">LLM Cost</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* How Outputs and Recommendations Work - Explainer Banner */}
        {execution.status === 'completed' && outputKeys.length > 0 && (
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Info className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 mb-1">How This Works</h3>
                  <p className="text-sm text-slate-600 mb-3">
                    The <strong>Output Predictions</strong> below show what the agent forecasts will happen if you apply its recommendations.
                    The agent has analyzed your data and generated <strong>actionable recommendations</strong> that you can view in Recommendations.
                  </p>
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/${currentOrganization?.slug}/agents`}
                      className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                    >
                      <Lightbulb className="h-4 w-4" />
                      View Recommendations
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-6 md:grid-cols-2">
          {/* Output Predictions - New Pure Function Model */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Output Predictions ({outputKeys.length})
              </CardTitle>
              <CardDescription>
                Metric predictions produced by the agent during this execution
              </CardDescription>
            </CardHeader>
            <CardContent>
              {outputKeys.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Activity className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">No outputs produced</p>
                  <p className="text-sm mt-1">The agent analyzed the data but produced no predictions</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {outputKeys.map((key) => {
                    const value = outputs[key]
                    const isNumeric = typeof value === 'number'

                    return (
                      <div
                        key={key}
                        className="rounded-lg border border-slate-200 overflow-hidden"
                      >
                        <div className="px-4 py-3 bg-slate-50 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                              <TrendingUp className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <span className="font-semibold text-slate-900 capitalize">
                                {key.replace(/_/g, ' ')}
                              </span>
                              <p className="text-xs text-slate-500">Predicted value</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`text-xl font-bold ${isNumeric ? 'text-blue-600' : 'text-slate-900'}`}>
                              {isNumeric ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(value)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Workflow Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Workflow Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Cycle ID</span>
                  <div className="flex items-center gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono cursor-help">
                          {execution.cycle_id.slice(0, 8)}...
                        </code>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="font-mono text-xs">{execution.cycle_id}</p>
                      </TooltipContent>
                    </Tooltip>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => copyToClipboard(execution.cycle_id)}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Started</span>
                  <span className="text-sm">
                    {execution.started_at ? new Date(execution.started_at).toLocaleString() : '-'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Completed</span>
                  <span className="text-sm">
                    {execution.completed_at ? new Date(execution.completed_at).toLocaleString() : '-'}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Error Message */}
            {execution.error_message && (
              <Card className="border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="text-base text-red-700 flex items-center gap-2">
                    <XCircle className="h-5 w-5" />
                    Error
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <pre className="text-sm text-red-600 whitespace-pre-wrap">
                    {execution.error_message}
                  </pre>
                </CardContent>
              </Card>
            )}

            {/* LLM Analysis */}
            {decision && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Brain className="h-5 w-5" />
                    AI Analysis
                  </CardTitle>
                  <CardDescription>Insights from the agent's analysis</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Summary / Analysis */}
                  {decision.analysis && (
                    <div className="p-3 bg-slate-50 rounded-lg border">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Summary</p>
                      <p className="text-sm text-slate-700 leading-relaxed">
                        {typeof decision.analysis === 'string'
                          ? decision.analysis
                          : JSON.stringify(decision.analysis, null, 2)}
                      </p>
                    </div>
                  )}

                  {/* Confidence Score */}
                  {decision.confidence !== undefined && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Confidence Score</span>
                        <span className="text-lg font-bold text-blue-700">
                          {typeof decision.confidence === 'number' ? (decision.confidence * 100).toFixed(0) : 0}%
                        </span>
                      </div>
                      <div className="w-full bg-blue-100 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${(typeof decision.confidence === 'number' ? decision.confidence : 0) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Reasoning */}
                  {decision.reasoning && (
                    <div className="p-3 bg-slate-50 rounded-lg border">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Reasoning</p>
                      <p className="text-sm text-slate-700 leading-relaxed">
                        {typeof decision.reasoning === 'string'
                          ? decision.reasoning
                          : JSON.stringify(decision.reasoning, null, 2)}
                      </p>
                    </div>
                  )}

                  {/* Expected Impact */}
                  {decision.expected_impact && (
                    <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                      <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2">Expected Impact</p>
                      {typeof decision.expected_impact === 'object' ? (
                        <div className="grid grid-cols-2 gap-2">
                          {Object.entries(decision.expected_impact).map(([key, value]) => (
                            <div key={key} className="flex items-center gap-2">
                              <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
                              <span className="text-sm text-emerald-700 capitalize">{key.replace(/_/g, ' ')}:</span>
                              <span className="text-sm font-medium text-emerald-900">{String(value)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-emerald-700">{String(decision.expected_impact)}</p>
                      )}
                    </div>
                  )}

                  {/* Risks */}
                  {decision.risks && Array.isArray(decision.risks) && decision.risks.length > 0 && (
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                      <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">Identified Risks</p>
                      <ul className="space-y-1.5">
                        {decision.risks.map((risk: any, i: number) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-amber-700">
                            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <span>{typeof risk === 'string' ? risk : JSON.stringify(risk)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  )
}
